import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { useTranslation } from 'react-i18next';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-16 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 cursor-pointer">{t('common:breadcrumb.home')}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800">{category.name}</span>
          </nav>

          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">{category.name}</h1>
            <p className="text-foreground-600">{category.description}</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:translate-y-[-2px]"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={`爆料网 - ${article.title}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {article.isHot && (
                    <span className="absolute top-3 right-3 bg-primary-500 text-background-50 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap">热门</span>
                  )}
                </div>
                <div className="p-5">
                  <h2 className="text-base font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                    {article.title}
                  </h2>
                  <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{article.viewCount.toLocaleString()}</span>
                    <span>{article.publishDate}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">暂无相关内容</p>
            </div>
          )}
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              name: `${category.name} - ${t('common:site.name')}`,
              description: category.description,
              url: `http://baoliao91.cn/category/${slug}`,
              isPartOf: {
                '@type': 'WebSite',
                name: t('common:site.name'),
                url: 'http://baoliao91.cn/',
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: t('common:breadcrumb.home'),
                  item: 'http://baoliao91.cn/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category.name,
                },
              ],
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: categoryArticles.map((article, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: `http://baoliao91.cn/article/${article.slug}`,
                name: article.title,
              })),
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}